import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import TextView from "../../../components/TextView";
import { colors } from "../../../utils";
import {
  CAPTURE_TYPES,
  captureTypeLabel,
  isAttached,
  attachedWheelsPreview,
  attachedWheelChipLabel,
  attachedWheelChipLabelWithExtra,
  numberedWheelTooltip,
  isEditableCaptureType,
} from "../Capture.helpers";
import { captureDisplayName, captureHasNotes } from "../captureDisplayName";
import {
  CapturePreviewThumb,
  CaptureMediaPlayer,
  CaptureProvenance,
} from "./CapturePreview";
import CaptureRowMenu from "./CaptureRowMenu";
import { formatDate } from "./captureWheelPicker";
import {
  Card,
  CardBody,
  CardMeta,
  CardMetaStart,
  CardMetaSpacer,
  CardMetaDate,
  TypeChip,
  AttachedChipButton,
  AttachedChipLabel,
  AttachedChipLabelText,
  AttachedChipExtra,
  CardAttachedFooter,
  CardActions,
  RowCheckbox,
  NotesGlyphBtn,
  DocLinkAnchor,
} from "../styles";

const NotesDialog = styled(Dialog)(() => ({
  ".MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  ".MuiPaper-root": {
    padding: "8px 8px 16px",
    border: "1px solid #E1E1E1",
    borderRadius: 12,
    width: 420,
    maxWidth: "92vw",
  },
}));

/**
 * One row in the Capture inbox.
 *
 * QA-78 (Gordon, 2026-08-04) removed the mode this card used to have:
 * "checkboxes should ALWAYS be visible — remove the 'select mode' / Done button
 * entirely, no need to enter a mode ... The checkbox belongs in the UPPER-LEFT
 * corner of each card, not vertically centered ... The add-to-wheel / edit /
 * delete icons should be PERSISTENT (always shown on each card), not
 * hover/select-gated."
 *
 * So there is no longer a `selectMode` prop: the checkbox and the action
 * trigger are both unconditional, and selection is just a boolean on the row.
 *
 * BUG-capture-8b690e (Gordon approved, 2026-08-13) later consolidated what
 * used to be up to four separate action icons (Detach / Edit / Add-to-Wheel /
 * Delete) into one persistent 3-dot overflow menu (CaptureRowMenu) — the
 * PERSISTENT requirement above still holds for the trigger button itself,
 * only the expanded action list is now click-gated. See CaptureRowMenu.jsx.
 */
const CaptureCard = ({
  item,
  onAttach,
  onCreateNew,
  onEdit,
  onDelete,
  onDetach,
  isDeleting,
  isDetaching,
  selected = false,
  onToggleSelect,
}) => {
  const { shown: shownWheel, extra: extraWheels } = attachedWheelsPreview(item);
  const attached = isAttached(item);
  const shownLabel = shownWheel ? attachedWheelChipLabel(shownWheel) : null;
  const chipLabel = shownWheel
    ? attachedWheelChipLabelWithExtra(shownWheel, extraWheels.length)
    : null;
  const editable = isEditableCaptureType(item.type);
  // QA-70: the row's name comes from the caption, else the file name stored in
  // media_key, else the type label — never the type label first.
  const primaryText = captureDisplayName(item);
  const createdLabel = formatDate(item?.created_at);
  const [notesOpen, setNotesOpen] = useState(false);
  const notes = typeof item?.text_content === "string" ? item.text_content.trim() : "";

  return (
    <Card selected={selected}>
      {/* QA-78 (1)+(2): always rendered, and `align-self: flex-start` in
          styles.RowCheckbox pins it to the card's UPPER-LEFT rather than
          centering it against the row's height. */}
      <RowCheckbox
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={`Select ${captureTypeLabel(item.type)} capture`}
        checked={selected}
        onClick={() => onToggleSelect?.(item)}
      >
        {selected && <CheckRoundedIcon style={{ fontSize: 16 }} />}
      </RowCheckbox>

      <CapturePreviewThumb item={item} />

      <CardBody>
        <CardMeta>
          <CardMetaStart>
          <TypeChip>{captureTypeLabel(item.type)}</TypeChip>
          {captureHasNotes(item) && (
              <Tooltip title="Notes">
                <NotesGlyphBtn
                  type="button"
                  aria-label="Notes"
                  onClick={() => setNotesOpen(true)}
                >
                  <NotesRoundedIcon
                    style={{ fontSize: 16, color: colors.textGrey, display: "block" }}
                  />
                </NotesGlyphBtn>
              </Tooltip>
            )}
          </CardMetaStart>
          {createdLabel && <CardMetaSpacer aria-hidden="true" />}
          {createdLabel && <CardMetaDate>{createdLabel}</CardMetaDate>}
        </CardMeta>

        <TextView
          variant="largeText"
          color={colors.black}
          dir="auto"
          style={{
            WebkitLineClamp: 2,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            /* A recovered file name has no spaces to wrap on, so a long one
               would otherwise push the actions column off the card. */
            overflowWrap: "anywhere",
          }}
        >
          {primaryText}
        </TextView>

        <CaptureMediaPlayer item={item} />
        {/* QA-99 — where this capture came from, when it came from somewhere. */}
        <CaptureProvenance item={item} />

        {item.type === CAPTURE_TYPES.DOC && item.media_url && (
          <DocLinkAnchor href={item.media_url} target="_blank" rel="noreferrer">
            <DescriptionOutlinedIcon style={{ fontSize: 14 }} /> Open document
          </DocLinkAnchor>
        )}

      </CardBody>

      {/* BUG-capture-8b690e (Gordon approved): the discrete Detach / Edit /
          Add-to-Wheel / Delete icons are consolidated into ONE persistent
          3-dot (kebab) overflow menu — cleaner rows, every action still
          reachable, order preserved (QA-81: edit-pen, wheel, trash; Detach
          leads when attached). See CaptureRowMenu.jsx for the full contract. */}
      <CardActions>
        <CaptureRowMenu
          item={item}
          attached={attached}
          editable={editable}
          isDeleting={isDeleting}
          isDetaching={isDetaching}
          onEdit={onEdit}
          onDelete={onDelete}
          onDetach={onDetach}
          onAttach={onAttach}
          onCreateNew={onCreateNew}
        />
      </CardActions>

      {shownWheel && (
        <CardAttachedFooter data-testid="capture-card-attached-footer">
          <Tooltip
            title={
              extraWheels.length > 0 ? (
                <span style={{ whiteSpace: "pre-line" }}>
                  {numberedWheelTooltip([shownWheel, ...extraWheels])}
                </span>
              ) : (
                shownLabel
              )
            }
          >
            <AttachedChipButton
              as="button"
              type="button"
              onClick={() => onAttach?.(item)}
              aria-label={
                extraWheels.length > 0
                  ? `${shownLabel} and ${extraWheels.length} more attached wheel${
                      extraWheels.length === 1 ? "" : "s"
                    }`
                  : shownLabel
              }
            >
              <AttachedChipLabel>
                <AttachedChipLabelText>
                  {extraWheels.length > 0 ? shownLabel : chipLabel}
                </AttachedChipLabelText>
                {extraWheels.length > 0 && (
                  <AttachedChipExtra>+{extraWheels.length}</AttachedChipExtra>
                )}
              </AttachedChipLabel>
            </AttachedChipButton>
          </Tooltip>
        </CardAttachedFooter>
      )}

      <NotesDialog
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        aria-labelledby="capture-notes-title"
      >
        <DialogTitle id="capture-notes-title">
          <TextView variant="title">Notes</TextView>
          <IconButton
            aria-label="close"
            onClick={() => setNotesOpen(false)}
            sx={{ position: "absolute", right: 8, top: 14, color: colors.darkGrey }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextView
            variant="largeText"
            color={colors.black}
            dir="auto"
            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {notes}
          </TextView>
        </DialogContent>
      </NotesDialog>
    </Card>
  );
};

export default CaptureCard;
